import { useEffect, useRef, useCallback } from 'react';
import { SignUpWithGoogleModel } from '../models';
import { loginWithGoogle } from '../services';
import { toast } from 'react-toastify';

declare global {
   interface Window {
      google: any;
   }
}

interface UseGoogleAuthOptions {
   onSuccess: (user: any) => void;
   onError?: (error: string) => void;
   buttonText?: 'signin_with' | 'signup_with';
}

export function useGoogleAuth({ onSuccess, onError, buttonText = 'signin_with' }: UseGoogleAuthOptions) {
   const googleButtonRef = useRef<HTMLDivElement>(null);

   const handleGoogleSignIn = useCallback(
      async (response: any) => {
         if (!response.credential) {
            const errorMsg = 'No credential received from Google';
            if (onError) {
               onError(errorMsg);
            } else {
               toast.error(errorMsg);
            }
            return;
         }

         const googleLoginData: SignUpWithGoogleModel = {
            idToken: response.credential,
         };

         try {
            const loginResponse = await loginWithGoogle(googleLoginData);

            if (loginResponse.error || !loginResponse.data) {
               const errorMsg = loginResponse.error?.message || 'Failed to authenticate with Google';
               if (onError) {
                  onError(errorMsg);
               } else {
                  toast.error(errorMsg);
               }
               return;
            }

            onSuccess(loginResponse.data);
         } catch (error) {
            const errorMsg = 'An unexpected error occurred';
            if (onError) {
               onError(errorMsg);
            } else {
               toast.error(errorMsg);
            }
         }
      },
      [onSuccess, onError]
   );

   useEffect(() => {
      if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
         console.warn('REACT_APP_GOOGLE_CLIENT_ID is not set');
         return;
      }

      // Check if script already exists
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');

      const initializeGoogleButton = () => {
         if (window.google && googleButtonRef.current) {
            try {
               // Clear any existing button
               googleButtonRef.current.innerHTML = '';

               window.google.accounts.id.initialize({
                  client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID!,
                  callback: handleGoogleSignIn,
               });

               window.google.accounts.id.renderButton(googleButtonRef.current, {
                  theme: 'outline',
                  size: 'large',
                  text: buttonText,
               });
            } catch (error) {
               console.error('Error initializing Google button:', error);
            }
         }
      };

      if (existingScript && window.google) {
         // Script already loaded, initialize immediately
         initializeGoogleButton();
      } else if (!existingScript) {
         // Load Google Identity Services script
         const script = document.createElement('script');
         script.src = 'https://accounts.google.com/gsi/client';
         script.async = true;
         script.defer = true;
         script.onload = initializeGoogleButton;
         script.onerror = () => {
            console.error('Failed to load Google Identity Services script');
         };
         document.body.appendChild(script);
      }
   }, [handleGoogleSignIn, buttonText]);

   return {
      googleButtonRef,
   };
}

